import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play } from 'lucide-react';
import { proxyImg } from '../lib/imgProxy';

/**
 * CinematicVideo — premium click-to-play YouTube/Vimeo player with custom poster.
 *
 * Props:
 *  - videoId / provider ('youtube' default | 'vimeo') / videoHash (vimeo unlisted)
 *  - title / posterSrc / kicker / caption / aspect / testId
 *
 * End-of-video behaviour: when playback finishes, the iframe is unmounted and
 * the poster reappears — this prevents YouTube's "up next" recommendation row
 * and Vimeo's end card from being shown at all.
 */
export const CinematicVideo = ({
  videoId,
  provider = 'youtube',
  videoHash,
  title,
  posterSrc,
  kicker,
  caption,
  aspect = 'aspect-video',
  testId = 'cinematic-video',
}) => {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef(null);
  const poster = proxyImg(
    posterSrc || `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
    { w: 1280, q: 78 }
  );
  // Fallback if maxres doesn't exist (older uploads): swap to hqdefault on error.
  const handlePosterError = (e) => {
    if (!e.target.dataset.fallbackTried && provider === 'youtube') {
      e.target.dataset.fallbackTried = '1';
      e.target.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
  };

  const embedSrc =
    provider === 'vimeo'
      ? `https://player.vimeo.com/video/${videoId}?${videoHash ? `h=${videoHash}&` : ''}autoplay=1&title=0&byline=0&portrait=0&dnt=1`
      : `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&fs=1&enablejsapi=1`;
  const externalUrl =
    provider === 'vimeo'
      ? `https://vimeo.com/${videoId}${videoHash ? `/${videoHash}` : ''}`
      : `https://www.youtube.com/watch?v=${videoId}`;
  const externalLabel = provider === 'vimeo' ? 'Open on Vimeo ↗' : 'Open on YouTube ↗';

  // Reset to poster when playback ends — kills end-screen recommendations.
  const resetToPoster = useCallback(() => setPlaying(false), []);

  // Listen to postMessage events from the iframe so we know when the video ended.
  useEffect(() => {
    if (!playing) return;

    // Subscribe to Vimeo's 'ended' event by posting a message to the player.
    if (provider === 'vimeo' && iframeRef.current) {
      const w = iframeRef.current.contentWindow;
      const subscribe = () => {
        try {
          w?.postMessage({ method: 'addEventListener', value: 'ended' }, '*');
        } catch {
          /* iframe not ready yet */
        }
      };
      // Try a few times until the player accepts the subscription
      const t1 = setTimeout(subscribe, 600);
      const t2 = setTimeout(subscribe, 1500);
      const t3 = setTimeout(subscribe, 3500);
      const cleanupTimers = () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };

      const onMessage = (e) => {
        if (!e.origin.includes('vimeo.com')) return;
        let data = e.data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { return; }
        }
        if (data && data.event === 'ended') resetToPoster();
      };
      window.addEventListener('message', onMessage);
      return () => {
        cleanupTimers();
        window.removeEventListener('message', onMessage);
      };
    }

    if (provider === 'youtube' && iframeRef.current) {
      const w = iframeRef.current.contentWindow;
      // YouTube IFrame API: client must "listen" before events flow.
      const subscribe = () => {
        try {
          w?.postMessage(
            JSON.stringify({ event: 'listening', id: testId, channel: 'widget' }),
            '*'
          );
        } catch {
          /* iframe not ready yet */
        }
      };
      const t1 = setTimeout(subscribe, 600);
      const t2 = setTimeout(subscribe, 1500);
      const t3 = setTimeout(subscribe, 3500);
      const cleanupTimers = () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };

      const onMessage = (e) => {
        if (!e.origin.includes('youtube')) return;
        let data = e.data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { return; }
        }
        // YT.PlayerState.ENDED === 0
        if (data && data.event === 'onStateChange' && data.info === 0) {
          resetToPoster();
        }
      };
      window.addEventListener('message', onMessage);
      return () => {
        cleanupTimers();
        window.removeEventListener('message', onMessage);
      };
    }
    return undefined;
  }, [playing, provider, testId, resetToPoster]);

  return (
    <figure
      className="cinematic-video group w-full"
      data-testid={testId}
    >
      {kicker && (
        <p className="text-[#00A86B] text-[11px] uppercase tracking-[0.4em] mb-5 text-center">
          {kicker}
        </p>
      )}

      <div
        className={`relative w-full ${aspect} overflow-hidden bg-black border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.7)]`}
      >
        {/* Hairline cinematic frame */}
        <div
          className="pointer-events-none absolute inset-2 border border-white/[0.06] z-10"
          aria-hidden="true"
        />

        {playing ? (
          <>
            <iframe
              ref={iframeRef}
              className="absolute inset-0 w-full h-full"
              src={embedSrc}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid={`${testId}-iframe`}
            />
            {/*
              Vimeo logo blocker — covers the top-right corner of the player
              where Vimeo's clickable logo button lives. Doesn't intercept the
              fullscreen toggle (bottom-right) or any playback controls.
              `pointer-events: auto` ensures clicks on the corner are swallowed
              by this transparent overlay instead of opening Vimeo in a new tab.
            */}
            <div
              aria-hidden="true"
              data-testid={`${testId}-vimeo-logo-blocker`}
              className="absolute top-0 right-0 w-16 h-12 sm:w-20 sm:h-14 z-20"
              style={{ pointerEvents: 'auto', background: 'transparent' }}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full block group/play focus:outline-none"
            aria-label={`Play: ${title}`}
            data-testid={`${testId}-play`}
          >
            <img
              src={poster}
              alt={title}
              onError={handlePosterError}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover/play:scale-[1.03]"
              loading="lazy"
            />
            {/* Cinematic vignette + dim */}
            <div
              className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-[radial-gradient(closest-side_at_50%_50%,transparent_0%,rgba(0,0,0,0.55)_85%)]"
              aria-hidden="true"
            />

            {/* Play affordance */}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-white/40 backdrop-blur-sm bg-black/40 transition-all duration-300 group-hover/play:bg-[#00A86B] group-hover/play:border-[#00A86B] group-hover/play:scale-110">
                <Play
                  className="w-8 h-8 sm:w-10 sm:h-10 text-white group-hover/play:text-black transition-colors translate-x-[2px]"
                  strokeWidth={1.5}
                  fill="currentColor"
                />
                <span
                  className="absolute inset-0 rounded-full border border-white/30 animate-ping opacity-60 group-hover/play:opacity-0"
                  aria-hidden="true"
                />
              </span>
            </span>

            {/* Bottom corner label */}
            <span className="absolute bottom-4 left-5 sm:bottom-6 sm:left-8 text-[10px] uppercase tracking-[0.35em] text-white/70 z-20">
              ▸ Watch
            </span>
            <span className="absolute bottom-4 right-5 sm:bottom-6 sm:right-8 text-[10px] uppercase tracking-[0.35em] text-white/70 z-20">
              Be The Train
            </span>
          </button>
        )}
      </div>

      {/* Fallback link intentionally removed per client request — the Play overlay opens the player inline */}

      {caption && (
        <figcaption className="text-white/40 text-xs uppercase tracking-[0.25em] mt-5 text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default CinematicVideo;
